import { modelLabel } from '@core/component/AI/constant/model-label';
import type { MagicChipStatus } from '@macro-inc/lexical-core';
import { markdownToPlainText } from '@macro-inc/lexical-core/utils/parsers';
import type {
  FoldedMessage,
  MessagePart,
  PendingInteraction,
  ToolName,
} from '@service-agent-fold/generated/types';
import { match, P } from 'ts-pattern';

/** The tool's own name, without its MCP server namespace. */
function toolLabel(name: ToolName): string {
  return name.kind === 'mcp' ? name.tool : name.name;
}

export type MagicChipActivity = {
  label: string;
  detail?: string;
  busy: boolean;
  tone?:
    | 'neutral'
    | 'active'
    | 'tool'
    | 'attention'
    | 'success'
    | 'failure'
    | 'stopped';
};

/** Who is answering, for the chip's header: the persona and its model. */
export type MagicChipHeader = {
  /** The persona's name, e.g. `Macro Agent`. */
  agent?: string;
  /** The model's display name, when the runtime has reported one. */
  model?: string;
  /** The pull request the session's work is on, once the runtime reports one. */
  pullRequestUrl?: string;
};

/**
 * A permission or question the agent is waiting on: the live interaction
 * from the session's metadata, and whether this viewer may answer (edit
 * access on the session) or is watching someone else be asked.
 */
export type MagicChipInteraction = {
  request: PendingInteraction;
  answering: boolean;
  action?: string;
  detail?: string;
  canAnswer: boolean;
};

/**
 * The one state the chip renders.
 *
 * Four: an activity line while the agent works with nothing to show, the
 * agent's latest passage as it is written with the activity alongside, a
 * question the agent has stopped to ask (with that passage beside it), and
 * the final passage alone once the turn ends. `markdown` is always the
 * turn's latest text chunk, never the whole turn.
 */
export type MagicChipPresentation =
  | { kind: 'working'; activity: MagicChipActivity }
  | { kind: 'answering'; markdown: string; activity: MagicChipActivity }
  | { kind: 'asking'; markdown: string; asking: MagicChipInteraction }
  | { kind: 'settled'; markdown: string };

export type MagicChipPresentationInput = {
  persistedStatus: MagicChipStatus;
  /**
   * The interaction the session is blocked on, when it belongs to this chip's
   * turn. The chip is a turn's surface, so a request made in a later turn
   * is that turn's chip's to show.
   */
  asking?: MagicChipInteraction;
  /**
   * Freshest lifecycle event seen on the live log stream, as its wire string.
   * A stopgap for {@link persistedStatus} being a snapshot from when the chip
   * was written: same vocabulary, staler. Goes away when the server pushes
   * status transitions (see {@link liveEventActivity}).
   */
  latestEvent?: string;
  prompt?: FoldedMessage;
  response?: FoldedMessage;
};

function toolActivity(
  part: Extract<MessagePart, { kind: 'tool_use' }>
): MagicChipActivity {
  const busy = part.status === 'pending' || part.status === 'running';
  const failed = part.status === 'failed';
  const label = toolLabel(part.name);
  return match(part.detail)
    .with({ kind: 'terminal' }, (detail) => ({
      label: failed
        ? 'Command failed'
        : busy
          ? 'Running command'
          : 'Command finished',
      detail: detail.command ?? label,
      busy,
    }))
    .with({ kind: 'edit' }, (detail) => ({
      label: failed ? 'Edit failed' : busy ? 'Editing files' : 'Files updated',
      detail: detail.diffs.at(-1)?.path ?? label,
      busy,
    }))
    .with({ kind: 'read' }, (detail) => ({
      label: failed
        ? 'Read failed'
        : busy
          ? 'Reading files'
          : 'Finished reading',
      detail: detail.paths.at(-1) ?? label,
      busy,
    }))
    .with(
      { kind: 'delete' },
      { kind: 'move' },
      { kind: 'search' },
      (detail) => ({
        label: failed ? `${label} failed` : label,
        detail: detail.paths.at(-1) ?? label,
        busy,
      })
    )
    .with({ kind: 'fetch' }, { kind: 'think' }, { kind: 'other' }, () => ({
      label: failed ? `${label} failed` : label,
      busy,
    }))
    .with({ kind: 'macro' }, { kind: 'view' }, () => ({
      label: failed ? `${label} failed` : busy ? `Using ${label}` : label,
      busy,
    }))
    .with({ kind: 'user_tool' }, (detail) => ({
      label:
        detail.outcome.kind === 'pending'
          ? `${label} drafted`
          : `${label} ${detail.outcome.kind.replace('_', ' ')}`,
      busy: false,
    }))
    .with({ kind: 'subagent' }, (detail) => {
      // What the subagent is doing right now says more than that it exists.
      const child = detail.children.findLast(
        (child) =>
          child.kind === 'tool_use' &&
          (child.status === 'pending' || child.status === 'running')
      );
      if (busy && child?.kind === 'tool_use') return toolActivity(child);
      return {
        label: failed
          ? 'Subagent failed'
          : busy
            ? 'Delegating work'
            : 'Subagent finished',
        detail: detail.title,
        busy,
      };
    })
    .exhaustive();
}

function partActivity(part: MessagePart): MagicChipActivity {
  return (
    match(part)
      .with({ kind: 'text' }, () => ({
        label: 'Writing response',
        busy: false,
      }))
      // A user's part, never an agent's; here only so the match stays total.
      .with({ kind: 'attachment' }, ({ name }) => ({
        label: 'File attached',
        detail: name,
        busy: false,
      }))
      .with({ kind: 'thought' }, ({ text }) => ({
        label: 'Thinking',
        detail: text.trim() || undefined,
        busy: true,
      }))
      .with({ kind: 'tool_use' }, (part) => ({
        ...toolActivity(part),
        tone: 'tool' as const,
      }))
      .with({ kind: 'permission', outcome: { kind: 'cancelled' } }, () => ({
        label: 'Permission cancelled',
        busy: false,
      }))
      .with({ kind: 'permission', outcome: { kind: 'selected' } }, () => ({
        label: 'Resuming work',
        busy: true,
      }))
      .with({ kind: 'permission', outcome: { kind: 'pending' } }, () => ({
        label: 'Permission needed',
        busy: false,
        tone: 'attention' as const,
      }))
      .with({ kind: 'permission', outcome: { kind: 'errored' } }, () => ({
        label: 'Permission failed',
        busy: false,
      }))
      .with({ kind: 'permission', outcome: { kind: 'unrecognized' } }, () => ({
        label: 'Permission unavailable',
        busy: false,
      }))
      .with({ kind: 'tool_approval', status: 'pending' }, () => ({
        label: 'Waiting for approval',
        busy: false,
        tone: 'attention' as const,
      }))
      .with({ kind: 'tool_approval', status: 'approved' }, () => ({
        label: 'Resuming work',
        busy: true,
      }))
      .with(
        {
          kind: 'tool_approval',
          status: P.union('denied', 'cancelled', 'expired'),
        },
        () => ({ label: 'Tool call not approved', busy: true })
      )
      .with({ kind: 'control', control: { kind: 'set_model' } }, (part) => ({
        label: 'Model changed',
        detail: modelLabel(part.control.model),
        busy: false,
      }))
      .with({ kind: 'control', control: { kind: 'compact' } }, () => ({
        label: 'Context compacted',
        busy: false,
      }))
      .with(
        { kind: 'control', control: { kind: 'set_config_option' } },
        ({ outcome }) => ({
          label:
            outcome.kind === 'rejected'
              ? 'Setting rejected'
              : outcome.kind === 'pending'
                ? 'Changing setting'
                : 'Setting changed',
          busy: false,
        })
      )
      .with({ kind: 'control', control: { kind: 'stop' } }, () => ({
        label: 'Stop requested',
        busy: false,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'pending' } }, () => ({
        label: 'Waiting for your input',
        busy: false,
        tone: 'attention' as const,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'accepted' } }, () => ({
        label: 'Resuming work',
        busy: true,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'completed' } }, () => ({
        label: 'Resuming work',
        busy: true,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'declined' } }, () => ({
        label: 'Question declined',
        busy: true,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'cancelled' } }, () => ({
        label: 'Question cancelled',
        busy: false,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'errored' } }, () => ({
        label: 'Question refused',
        busy: false,
      }))
      .with({ kind: 'elicitation', outcome: { kind: 'unrecognized' } }, () => ({
        label: 'Question answered',
        busy: true,
      }))
      // The agent wakes to answer a background task's report.
      .with({ kind: 'task_notification' }, (part) => ({
        label:
          part.status === 'failed'
            ? 'Background task failed'
            : 'Background task update',
        detail: part.summary || undefined,
        busy: true,
      }))
      .with({ kind: 'plan' }, ({ entries }) => {
        const completed = entries.filter(
          (entry) => entry.status === 'completed'
        ).length;
        const current = entries.find((entry) => entry.status === 'in_progress');
        return {
          label: `Todos ${completed}/${entries.length}`,
          detail: current?.content,
          busy: completed < entries.length,
        };
      })
      .exhaustive()
  );
}

/** How the turn ended, when it has — every ending but a clean answer. */
function turnEndedActivity(
  response: FoldedMessage | undefined
): MagicChipActivity | undefined {
  const stop = response?.stop;
  if (!stop) return undefined;
  const activity = match(stop)
    .with({ kind: 'end_turn' }, () => ({
      // A clean end with prose settles before activity is consulted, so
      // reaching this arm means the agent closed the turn empty-handed.
      label: 'Agent finished without a response',
      busy: false,
    }))
    .with({ kind: 'cancelled' }, () => ({ label: 'Stopped', busy: false }))
    .with({ kind: 'refusal' }, () => ({
      label: 'Request refused',
      busy: false,
    }))
    .with({ kind: 'max_tokens' }, () => ({
      label: 'Response limit reached',
      busy: false,
    }))
    .with({ kind: 'max_turn_requests' }, () => ({
      label: 'Turn limit reached',
      busy: false,
    }))
    .with({ kind: 'other' }, ({ reason }) => ({ label: reason, busy: false }))
    // A failure the runtime wrote in the person's terms — a spent Cursor
    // budget — is shown in those terms; the link lives in the session,
    // which the chip opens.
    .with({ kind: 'failed', notice: P.nonNullable }, ({ notice }) => ({
      label: notice.title,
      detail: notice.body,
      busy: false,
    }))
    // The runtime errored the prompt. The label says that much; the
    // runtime's own message goes in the detail line, because some of these
    // are the user's to act on — a repository Cursor cannot reach, say.
    .with({ kind: 'failed' }, ({ message }) => ({
      label: "Agent couldn't answer",
      detail: message,
      busy: false,
    }))
    .exhaustive();
  const tone = match(stop.kind)
    .with('end_turn', () => 'success' as const)
    .with('cancelled', () => 'stopped' as const)
    .otherwise(() => 'failure' as const);
  return { ...activity, tone };
}

/**
 * What the agent is doing right now, from the parts of an open turn: an
 * unanswered permission request or question outranks a running tool, which
 * outranks whatever arrived last.
 */
function turnInFlightActivity(
  response: FoldedMessage | undefined
): MagicChipActivity | undefined {
  if (!response) return undefined;
  const blocked = response.parts.findLast(
    (part) =>
      (part.kind === 'permission' &&
        (part.outcome.kind === 'pending' ||
          part.outcome.kind === 'errored' ||
          part.outcome.kind === 'unrecognized')) ||
      (part.kind === 'elicitation' && part.outcome.kind === 'pending')
  );
  const runningTool = response.parts.findLast(
    (part) =>
      part.kind === 'tool_use' &&
      (part.status === 'pending' || part.status === 'running')
  );
  // The fold never derives a message with no parts, so an existing response
  // always has a latest part to describe.
  const latest = response.parts.at(-1);
  return partActivity((blocked ?? runningTool ?? latest)!);
}

/** The session's persisted lifecycle, when the fold has nothing livelier. */
function statusActivity(status: MagicChipStatus): MagicChipActivity {
  return match(status)
    .with('no_messages', () => ({
      label: 'Queued',
      busy: false,
      tone: 'neutral' as const,
    }))
    .with('booting', () => ({
      label: 'Booting agent',
      detail: 'Preparing workspace',
      busy: true,
    }))
    .with('acp_ready', () => ({ label: 'Waiting for harness', busy: true }))
    .with('shutting_down', () => ({ label: 'Wrapping up', busy: false }))
    .with('disconnected', () => ({
      label: 'Session disconnected',
      busy: false,
      tone: 'stopped' as const,
    }))
    .exhaustive(() => ({
      label: 'Queued',
      busy: false,
      tone: 'neutral' as const,
    }));
}

/**
 * Lifecycle read off the live log stream — the fresher twin of two
 * {@link statusActivity} arms, and the file's one dependence on raw log
 * frames. Both callers delete together once the server pushes status
 * transitions and `latestEvent` leaves the input.
 */
function liveEventActivity(
  latestEvent: string | undefined,
  name: 'disconnected' | 'acp_ready'
): MagicChipActivity | undefined {
  if (latestEvent !== name) return undefined;
  return statusActivity(name);
}

/**
 * The agent's latest prose: the last text part of the turn, which the fold
 * appends into as chunks land. A passage written before a tool ran gives way
 * to what the agent says after it, and when the turn ends cleanly the final
 * passage is what stays.
 */
function latestChunk(response: FoldedMessage | undefined): string {
  return (
    response?.parts.findLast(
      (part): part is Extract<MessagePart, { kind: 'text' }> =>
        part.kind === 'text' && Boolean(part.text.trim())
    )?.text ?? ''
  );
}

/**
 * Markdown flattened to the single line the console's output row shows.
 *
 * The row is one line of prose, so the block structure goes: fences, list
 * bullets, heading hashes and quote marks are dropped and every run of
 * whitespace becomes one space. Emphasis and inline code markers go too -
 * the row has no formatting to carry them. Macro's own `<m-*>` tags are
 * resolved first, so a mention reads as its name rather than its JSON.
 *
 * An underscore between two word characters is left alone: agents write
 * about `turn_ended` and `agent_fold` far more often than they emphasise a
 * word, and stripping those would rename the thing being discussed.
 */
export function flattenToLine(markdown: string): string {
  return markdownToPlainText(markdown)
    .replace(/^\s*```[^\n]*$/gm, ' ')
    .replace(/^\s{0,3}(?:[-*+]|\d+[.)])\s+/gm, '')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/[*~`]/g, '')
    .replace(/_+/g, (run, at: number, text: string) =>
      isWordChar(text[at - 1]) && isWordChar(text[at + run.length]) ? run : ''
    )
    .replace(/\s+/g, ' ')
    .trim();
}

function isWordChar(character: string | undefined): boolean {
  return character !== undefined && /[A-Za-z0-9]/.test(character);
}

/**
 * The one line the console's output row shows, or `undefined` when the turn
 * has written nothing yet and the row should say so itself.
 *
 * A turn that has stopped to ask shows the question rather than the prose
 * before it: the question is why the chip is sitting there.
 */
export function presentationLine(
  presentation: MagicChipPresentation
): string | undefined {
  if (presentation.kind === 'asking') {
    const { request, action } = presentation.asking;
    const asked =
      request.kind === 'elicitation'
        ? request.message
        : action || 'Open session to respond';
    return asked || undefined;
  }
  if (presentation.kind === 'working') return undefined;
  return flattenToLine(presentation.markdown) || undefined;
}

export function presentationStatus(
  presentation: MagicChipPresentation
): MagicChipActivity {
  return match(presentation)
    .with({ kind: 'working' }, { kind: 'answering' }, (p) => p.activity)
    .with({ kind: 'asking' }, ({ asking }) => ({
      label: asking.answering
        ? 'Sending answer'
        : asking.request.kind === 'tool_approval'
          ? 'Waiting for approval'
          : asking.canAnswer
            ? 'Waiting for you'
            : 'Waiting for an editor',
      busy: false,
    }))
    .with({ kind: 'settled' }, () => ({ label: 'Done', busy: false }))
    .exhaustive();
}

/** Project fold and lifecycle facts into the one state the view renders. */
export function deriveMagicChipPresentation(
  input: MagicChipPresentationInput
): MagicChipPresentation {
  const { response, prompt, latestEvent, persistedStatus, asking } = input;

  const markdown = latestChunk(response);
  if (response?.stop?.kind === 'end_turn' && markdown) {
    return { kind: 'settled', markdown };
  }
  // A question the agent is waiting on outranks whatever else the turn is
  // doing: nothing moves until it is answered.
  if (asking) {
    return { kind: 'asking', markdown, asking };
  }

  // Best available answer first: how the turn ended, then what it is doing,
  // then that it exists at all, then the session's lifecycle — live before
  // persisted.
  const activity =
    turnEndedActivity(response) ??
    liveEventActivity(latestEvent, 'disconnected') ??
    turnInFlightActivity(response) ??
    (prompt
      ? { label: 'Waiting for agent', busy: true, tone: 'neutral' as const }
      : undefined) ??
    liveEventActivity(latestEvent, 'acp_ready') ??
    statusActivity(persistedStatus);

  // Prose the turn has not closed on yet. The fold appends into the trailing
  // text part as chunks land, so this is the answer being written, and the
  // activity stays alongside it to say what the agent is doing between
  // sentences.
  if (markdown) return { kind: 'answering', markdown, activity };

  return { kind: 'working', activity };
}
